import { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import type { HoldingRevenueBreakdown } from '@monitoring-panel/types';
import { formatNumber } from '@/lib/utils';
import {
  ADVIA_CHART_OPTS,
  ADVIA_CHART_TOKENS,
  ADVIA_ECHARTS_THEME,
  HIERARCHY_LEVEL_COLORS,
  tooltipRow,
  tooltipTitle,
} from '@/lib/advia-charts';

const LEVEL_LEGEND = [
  { label: 'Holdings', color: HIERARCHY_LEVEL_COLORS.holding },
  { label: 'Agencies', color: HIERARCHY_LEVEL_COLORS.agency },
  { label: 'Accounts', color: HIERARCHY_LEVEL_COLORS.account },
] as const;

interface HoldingsBarChartProps {
  data: HoldingRevenueBreakdown[];
}

export function HoldingsBarChart({ data }: HoldingsBarChartProps) {
  const chartOption: EChartsOption = useMemo(() => {
    if (!data || data.length === 0) return {};

    // Build Sankey nodes and links
    const nodes: Array<{ name: string; depth?: number }> = [];
    const links: Array<{ source: string; target: string; value: number }> = [];
    const nodeSet = new Set<string>();

    // Add nodes and links for Holdings → Agencies → Accounts
    // To avoid cycles, we need to ensure node names are unique across levels
    data.forEach((holding) => {
      const holdingName = `H: ${holding.holding}`;

      if (!nodeSet.has(holdingName)) {
        nodes.push({ name: holdingName, depth: 0 });
        nodeSet.add(holdingName);
      }

      holding.agencies.forEach((agency) => {
        const agencyName = `A: ${agency.agency}`;

        if (!nodeSet.has(agencyName)) {
          nodes.push({ name: agencyName, depth: 1 });
          nodeSet.add(agencyName);
        }

        // Link from holding to agency
        const holdingToAgencyValue = agency.revenue + agency.forecastedRevenue;
        if (holdingToAgencyValue > 0) {
          links.push({
            source: holdingName,
            target: agencyName,
            value: holdingToAgencyValue,
          });
        }

        // Add account nodes and links
        agency.accounts.forEach((account) => {
          const accountName = `Acc: ${account.account}`;

          if (!nodeSet.has(accountName)) {
            nodes.push({ name: accountName, depth: 2 });
            nodeSet.add(accountName);
          }

          const agencyToAccountValue = account.revenue + account.forecastedRevenue;
          if (agencyToAccountValue > 0) {
            links.push({
              source: agencyName,
              target: accountName,
              value: agencyToAccountValue,
            });
          }
        });
      });
    });

    return {
      tooltip: {
        trigger: 'item',
        triggerOn: 'mousemove',
        formatter: (params: any) => {
          if (params.dataType === 'edge') {
            return (
              tooltipTitle(`${params.data.source} → ${params.data.target}`) +
              tooltipRow('Revenue', formatNumber(params.data.value, 'euro'))
            );
          } else {
            return (
              tooltipTitle(params.name) +
              tooltipRow('Total Revenue', formatNumber(params.value, 'euro'))
            );
          }
        },
      },
      series: [
        {
          type: 'sankey',
          data: nodes,
          links: links,
          emphasis: {
            focus: 'adjacency',
          },
          lineStyle: {
            curveness: 0.5,
          },
          label: {
            fontSize: 11,
            color: ADVIA_CHART_TOKENS.textPrimary,
            fontFamily: ADVIA_CHART_TOKENS.fontSans,
            fontWeight: 600,
            show: true,
          },
          levels: [
            {
              depth: 0,
              itemStyle: {
                color: HIERARCHY_LEVEL_COLORS.holding,
              },
              lineStyle: {
                color: 'source',
                opacity: 0.35,
              },
              label: {
                fontSize: 12,
                fontWeight: 'bold',
                show: true,
              },
            },
            {
              depth: 1,
              itemStyle: {
                color: HIERARCHY_LEVEL_COLORS.agency,
              },
              lineStyle: {
                color: 'source',
                opacity: 0.35,
              },
              label: {
                fontSize: 11,
                show: true,
              },
            },
            {
              depth: 2,
              itemStyle: {
                color: HIERARCHY_LEVEL_COLORS.account,
              },
              lineStyle: {
                color: 'source',
                opacity: 0.35,
              },
              label: {
                fontSize: 11,
                show: true,
              },
            },
          ],
          nodeGap: 12,
          nodeWidth: 20,
          layoutIterations: 32,
        },
      ],
    };
  }, [data]);

  if (!data || data.length === 0) {
    return (
      <div className="adv-card adv-state font-adv-sans">
        <p className="adv-state__desc">No holdings data available</p>
      </div>
    );
  }

  return (
    <div className="adv-card p-6 font-adv-sans">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h2 className="adv-section-title">
            Revenue Flow: Holdings → Agencies → Accounts
          </h2>
          <div className="flex items-center gap-4 text-adv-body-s text-adv-secondary">
            {LEVEL_LEGEND.map(({ label, color }) => (
              <div key={label} className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
                <span>{label}</span>
              </div>
            ))}
          </div>
        </div>
        <p className="adv-section-desc">
          Sankey diagram showing revenue flow through the organizational hierarchy. Width of flows represents revenue volume.
        </p>
      </div>
      <ReactECharts
        option={chartOption}
        style={{ height: 800 }}
        theme={ADVIA_ECHARTS_THEME}
        opts={ADVIA_CHART_OPTS}
      />
    </div>
  );
}
